import * as Phaser from "phaser"
import { TEX } from "../core/keys"
import { THEME } from "../core/theme"
import { GAME_WIDTH } from "../data/constants"

const COLOR = THEME.colors
const FONT = THEME.fonts

const NEON = {
  cyan: 0x00f0ff,
  magenta: 0xff00ff,
  pink: 0xff2d95,
  gold: 0xfbbf24,
  green: 0x39ff14,
  blue: 0x4d8dff,
  purple: 0xb24dff,
  red: 0xff2222,
}

/** Explosion animation keys mapped by size category */
const EXPLOSION_ANIMS = {
  micro: "anim_explosion_micro",
  small: "anim_explosion_small",
  medium: "anim_explosion_medium",
  large: "anim_explosion_large",
  debris: "anim_explosion_debris",
} as const

type CaptureFrameObject = Phaser.GameObjects.GameObject &
  Phaser.GameObjects.Components.Depth

type CaptureFrameFactory = Phaser.GameObjects.GameObjectFactory & {
  captureFrame: (key: string) => CaptureFrameObject
}

export class VfxManager {
  private scene: Phaser.Scene

  public explosionEmitter!: Phaser.GameObjects.Particles.ParticleEmitter
  public bumperEmitter!: Phaser.GameObjects.Particles.ParticleEmitter
  public sparkEmitter!: Phaser.GameObjects.Particles.ParticleEmitter
  public trailEmitter!: Phaser.GameObjects.Particles.ParticleEmitter
  public pickupEmitter!: Phaser.GameObjects.Particles.ParticleEmitter
  public jetpackFlames!: Phaser.GameObjects.Particles.ParticleEmitter
  public muzzleEmitter!: Phaser.GameObjects.Particles.ParticleEmitter
  public smokeEmitter!: Phaser.GameObjects.Particles.ParticleEmitter

  private heroLaunchFlames!: Phaser.GameObjects.Particles.ParticleEmitter
  private heroLaunchTrail!: Phaser.GameObjects.Particles.ParticleEmitter

  private screenFlash!: Phaser.GameObjects.Rectangle
  private captureKey = "vfx_capture_frame"
  private captureIndex = 0

  // ── Persistent Trails ──────────────────────────────────────────
  private trailTexture?: Phaser.Textures.DynamicTexture | null
  private trailOverlay?: Phaser.GameObjects.Image
  private readonly TRAIL_TEX_KEY = "vfx_persistent_trails"

  // ── Base Camera FX (always-on during gameplay) ──────────────────
  private baseCameraVignette: Phaser.Filters.Vignette | null = null
  private baseCameraColorMatrix: Phaser.Filters.ColorMatrix | null = null
  private comboEscalationMatrix: Phaser.Filters.ColorMatrix | null = null

  constructor(scene: Phaser.Scene) {
    this.scene = scene
  }

  /** Safe access to the main camera, guarded against scene shutdown/transition states. */
  private get mainCamera(): Phaser.Cameras.Scene2D.Camera | null {
    return this.scene.cameras?.main || null
  }

  public createSystems(): void {
    const cam = this.mainCamera
    if (!cam) return

    // Enable composite mode for CaptureFrame usage
    cam.setForceComposite(true)

    // Create persistent trail texture
    this.trailTexture = this.scene.textures.addDynamicTexture(
      this.TRAIL_TEX_KEY,
      1280,
      720,
    )
    if (this.trailTexture) {
      this.trailOverlay = this.scene.add.image(640, 360, this.TRAIL_TEX_KEY)
      this.trailOverlay.setDepth(10)
      this.trailOverlay.setAlpha(0.6)
      this.trailOverlay.setBlendMode(Phaser.BlendModes.ADD)
      this.trailOverlay.setScrollFactor(0)
    }

    this.jetpackFlames = this.createJetpackFlameEmitter()
    this.heroLaunchFlames = this.createJetpackFlameEmitter()

    // Explosion particles - punchier colors
    this.explosionEmitter = this.scene.add.particles(0, 0, TEX.PARTICLE, {
      speed: { min: 120, max: 350 },
      angle: { min: 0, max: 360 },
      scale: { start: 1.5, end: 0 },
      lifespan: { min: 400, max: 800 },
      tint: [0xff00ff, 0x00ffff, 0xffff00], // Neon Magenta, Cyan, Yellow
      blendMode: "ADD",
      emitting: false,
    })

    // Bumper hit particles
    this.bumperEmitter = this.scene.add.particles(0, 0, TEX.SPARK, {
      speed: { min: 180, max: 400 },
      angle: { min: 0, max: 360 },
      scale: { start: 1.2, end: 0 },
      lifespan: { min: 250, max: 500 },
      tint: [0x00ff00, 0xffff00, 0xffffff], // Toxic Green, Yellow, White
      blendMode: "ADD",
      emitting: false,
    })

    // Launcher spark particles
    this.sparkEmitter = this.scene.add.particles(0, 0, TEX.SPARK, {
      speed: { min: 50, max: 150 },
      angle: { min: 0, max: 360 },
      scale: { start: 1.0, end: 0 },
      lifespan: { min: 150, max: 300 },
      tint: [0xffffff, 0x00ffff, 0xff00ff],
      blendMode: "ADD",
      emitting: false,
    })

    this.trailEmitter = this.createTrailEmitter()
    this.heroLaunchTrail = this.createTrailEmitter()

    this.muzzleEmitter = this.scene.add.particles(0, 0, TEX.FLASH, {
      speed: { min: 30, max: 100 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.9, end: 0 },
      lifespan: { min: 60, max: 120 },
      tint: [COLOR.white, COLOR.amberSoft],
      blendMode: "ADD",
      emitting: false,
    })

    this.smokeEmitter = this.scene.add.particles(0, 0, TEX.SMOKE, {
      speed: { min: 8, max: 28 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.5, end: 1.3 },
      alpha: { start: 0.22, end: 0 },
      lifespan: { min: 260, max: 420 },
      tint: [COLOR.mutedBlue, COLOR.mutedBlueDark, COLOR.mutedBlueLight],
      blendMode: "NORMAL",
      emitting: false,
    })

    // Pickup particles
    this.pickupEmitter = this.scene.add.particles(0, 0, TEX.PARTICLE, {
      speed: { min: 30, max: 80 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.6, end: 0 },
      lifespan: { min: 300, max: 500 },
      tint: [COLOR.white, COLOR.successBright, COLOR.success],
      blendMode: "ADD",
      emitting: false,
    })

    // Screen flash rectangle
    this.screenFlash = this.scene.add.rectangle(
      cam.centerX,
      cam.centerY,
      cam.width,
      cam.height,
      COLOR.white,
      0,
    )
    this.screenFlash.setScrollFactor(0)
    this.screenFlash.setDepth(2000)
  }

  private createJetpackFlameEmitter(): Phaser.GameObjects.Particles.ParticleEmitter {
    // Jetpack exhaust should read as a compact hot plume, not a second projectile.
    return this.scene.add.particles(0, 0, TEX.FIRE, {
      speed: { min: 35, max: 95 },
      angle: { min: 155, max: 205 },
      scale: { start: 1.6, end: 0.08 },
      lifespan: { min: 90, max: 160 },
      tint: [0xfef08a, 0xfbbf24, 0xf97316, 0xef4444],
      alpha: { start: 0.9, end: 0 },
      blendMode: "ADD",
      frequency: 10,
      quantity: 2,
      emitting: false,
    })
  }

  private createTrailEmitter(): Phaser.GameObjects.Particles.ParticleEmitter {
    return this.scene.add.particles(0, 0, TEX.PARTICLE, {
      speed: { min: 10, max: 30 },
      scale: { start: 0.7, end: 0 },
      lifespan: { min: 150, max: 250 },
      alpha: { start: 0.9, end: 0 },
      tint: 0x00ffff,
      blendMode: "ADD",
      emitting: false,
    })
  }

  private clearEmitter(
    emitter: Phaser.GameObjects.Particles.ParticleEmitter,
  ): void {
    emitter.stopFollow()
    emitter.emitting = false
    emitter.killAll()
  }

  public startHeroLaunchExhaust(target: Phaser.Types.Math.Vector2Like): void {
    this.clearEmitter(this.jetpackFlames)

    this.clearEmitter(this.heroLaunchFlames)
    this.heroLaunchFlames.setPosition(0, 0)
    this.heroLaunchFlames.startFollow(target, -38, 6, true)
    this.heroLaunchFlames.emitting = true
    this.heroLaunchFlames.frequency = 4
    this.heroLaunchFlames.quantity = 4

    this.clearEmitter(this.heroLaunchTrail)
    this.heroLaunchTrail.setPosition(0, 0)
    this.heroLaunchTrail.startFollow(target, -44, 8, true)
    this.heroLaunchTrail.emitting = true
    this.heroLaunchTrail.frequency = 6
    this.heroLaunchTrail.quantity = 2
  }

  public stopHeroLaunchExhaust(): void {
    this.heroLaunchFlames.stopFollow()
    this.heroLaunchFlames.emitting = false
    this.heroLaunchTrail.stopFollow()
    this.heroLaunchTrail.emitting = false
  }

  public emitJetpackFlameBurst(count: number, x: number, y: number): void {
    const emitter = this.jetpackFlames
    const follow = emitter.follow
    const followOffsetX = emitter.followOffset.x
    const followOffsetY = emitter.followOffset.y
    const trackVisible = emitter.trackVisible

    if (follow) emitter.stopFollow()
    emitter.setPosition(x, y)
    emitter.explode(count)

    if (follow) {
      emitter.setPosition(0, 0)
      emitter.startFollow(follow, followOffsetX, followOffsetY, trackVisible)
    }
  }

  /**
   * Sustains a screen color overlay for a duration, then fades out.
   * Fades in quickly (180ms), holds, then fades out over fadeMs.
   */
  public sustainedScreenTint(
    color: number,
    alpha: number,
    holdMs: number,
    fadeMs = 400,
  ): void {
    this.scene.tweens.killTweensOf(this.screenFlash)
    this.screenFlash.setFillStyle(color)
    this.screenFlash.setAlpha(0)
    this.scene.tweens.add({
      targets: this.screenFlash,
      alpha,
      duration: 180,
      ease: "Quad.easeOut",
      onComplete: () => {
        this.scene.time.delayedCall(holdMs, () => {
          if (!this.screenFlash?.active) return
          this.scene.tweens.add({
            targets: this.screenFlash,
            alpha: 0,
            duration: fadeMs,
            ease: "Quad.easeIn",
          })
        })
      },
    })
  }

  /**
   * Applies a camera-wide heat haze effect for a duration using live simplex
   * noise as the displacement source. The noise scrolls and flows over time,
   * producing genuine organic shimmer. Enveloped so it fades in and out.
   */
  public heatHazePulse(intensity = 0.018, durationMs = 2500): void {
    const cam = this.mainCamera
    if (!cam) return

    // Unique key per pulse so concurrent calls don't share the same texture
    const noiseKey = `haze_${this.scene.time.now}`

    const noiseObj = this.scene.add
      .noisesimplex2d(
        { noiseCells: [6, 6], noiseIterations: 2, noiseNormalMap: true },
        0,
        0,
        160,
        90,
      )
      .setRenderToTexture(noiseKey)

    const disp = cam.filters.external.addDisplacement(noiseKey, 0, 0)
    const startTime = this.scene.time.now

    const ticker = this.scene.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        const elapsed = this.scene.time.now - startTime
        const progress = elapsed / durationMs
        const envelope = Math.sin(Math.min(progress, 1) * Math.PI)
        noiseObj.noiseOffset = [-elapsed * 0.0003, -elapsed * 0.0005]
        noiseObj.noiseFlow = (elapsed * 0.0006) % (Math.PI * 2)
        disp.x = intensity * envelope
        disp.y = intensity * 0.6 * envelope
      },
    })

    this.scene.time.delayedCall(durationMs, () => {
      ticker.remove()
      // Scene may have shut down before this timer fires (e.g. wave end).
      // Avoid crashing Phaser's texture manager by bailing out early.
      if (!noiseObj.active || !this.scene.sys.isActive()) {
        return
      }
      cam.filters.external.remove(disp)
      noiseObj.destroy()
      if (this.scene.textures.exists(noiseKey)) {
        this.scene.textures.remove(noiseKey)
      }
    })
  }

  public flashScreen(
    color: number = COLOR.white,
    alpha = 0.3,
    duration = 100,
  ): void {
    this.screenFlash.setFillStyle(color)
    this.screenFlash.setAlpha(alpha)
    this.scene.tweens.add({
      targets: this.screenFlash,
      alpha: 0,
      duration,
      ease: "Quad.easeOut",
    })
  }

  public shakeCamera(duration = 150, intensity = 0.015): void {
    this.mainCamera?.shake(duration, intensity)
  }

  public emitBumperRewardPulse(
    x: number,
    y: number,
    color: number,
    power = 1,
  ): void {
    const rayCount = 10
    const innerRadius = 12 * power
    const outerRadius = 32 * power
    const burst = this.scene.add.graphics({ x, y })
    burst.setBlendMode(Phaser.BlendModes.ADD)
    burst.setDepth(98)

    burst.lineStyle(5, COLOR.white, 0.95)
    burst.strokeCircle(0, 0, innerRadius)
    burst.lineStyle(3, color, 0.95)
    burst.strokeCircle(0, 0, innerRadius + 7 * power)
    burst.lineStyle(2, color, 0.8)
    burst.strokeCircle(0, 0, innerRadius + 15 * power)

    for (let i = 0; i < rayCount; i++) {
      const angle = (Math.PI * 2 * i) / rayCount
      const inset = innerRadius + (i % 2) * 3 * power
      const length = outerRadius + (i % 2) * 8 * power
      burst.lineStyle(
        i % 2 === 0 ? 4 : 2,
        i % 2 === 0 ? COLOR.white : color,
        0.9,
      )
      burst.beginPath()
      burst.moveTo(Math.cos(angle) * inset, Math.sin(angle) * inset)
      burst.lineTo(Math.cos(angle) * length, Math.sin(angle) * length)
      burst.strokePath()
    }

    const glow = this.scene.add.circle(x, y, 18 * power, color, 0.22)
    glow.setBlendMode(Phaser.BlendModes.ADD)
    glow.setDepth(96)

    const core = this.scene.add.circle(x, y, 7 * power, COLOR.white, 0.9)
    core.setBlendMode(Phaser.BlendModes.ADD)
    core.setDepth(99)

    this.scene.tweens.add({
      targets: burst,
      scaleX: 1.65,
      scaleY: 1.65,
      rotation: 0.22,
      alpha: 0,
      duration: 240,
      ease: "Expo.easeOut",
      onComplete: () => burst.destroy(),
    })

    this.scene.tweens.add({
      targets: glow,
      scaleX: 2.4,
      scaleY: 2.4,
      alpha: 0,
      duration: 220,
      ease: "Quad.easeOut",
      onComplete: () => glow.destroy(),
    })

    this.scene.tweens.add({
      targets: core,
      scaleX: 0.45,
      scaleY: 0.45,
      alpha: 0,
      duration: 110,
      ease: "Quad.easeIn",
      onComplete: () => core.destroy(),
    })
  }

  public emitMuzzleFlash(
    x: number,
    y: number,
    angle: number,
    color: number,
    power = 1,
  ): void {
    this.muzzleEmitter.explode(Math.max(4, Math.round(8 * power)), x, y)

    // Primary flash ellipse
    const flash = this.scene.add.ellipse(
      x,
      y,
      24 * power,
      10 * power,
      color,
      0.5,
    )
    flash.setRotation(angle)
    flash.setBlendMode(Phaser.BlendModes.ADD)
    flash.setDepth(90)
    this.scene.tweens.add({
      targets: flash,
      scaleX: 1.8,
      scaleY: 0.18,
      alpha: 0,
      duration: 90,
      ease: "Quad.easeOut",
      onComplete: () => flash.destroy(),
    })

    // White-hot core flash
    const core = this.scene.add.circle(x, y, 4 * power, COLOR.white, 0.7)
    core.setBlendMode(Phaser.BlendModes.ADD)
    core.setDepth(91)
    this.scene.tweens.add({
      targets: core,
      scaleX: 0.2,
      scaleY: 0.2,
      alpha: 0,
      duration: 60,
      ease: "Quad.easeOut",
      onComplete: () => core.destroy(),
    })
  }

  /**
   * Intense firing VFX used during overdrive.
   * Combines muzzle flash, high-velocity sparks, and chromatic plasma flares.
   */
  public emitOverdriveFire(x: number, y: number, angle: number): void {
    const angleRad = angle // already in rads based on how it is called usually, wait wave-scene passes rads

    // 1. Massive particle burst
    this.muzzleEmitter.explode(16, x, y)
    this.sparkEmitter.explode(12, x, y)
    this.explosionEmitter.explode(5, x, y) // Small explosive kick

    // 2. Primary cyan muzzle flash (long and sharp)
    const flash = this.scene.add.ellipse(x, y, 64, 16, NEON.cyan, 0.6)
    flash.setRotation(angleRad)
    flash.setBlendMode(Phaser.BlendModes.ADD)
    flash.setDepth(110)
    this.scene.tweens.add({
      targets: flash,
      scaleX: 2.2,
      scaleY: 0.1,
      alpha: 0,
      duration: 120,
      ease: "Cubic.easeOut",
      onComplete: () => flash.destroy(),
    })

    // 3. Secondary magenta plasma flare (offset forward)
    const offX = Math.cos(angleRad) * 20
    const offY = Math.sin(angleRad) * 20
    const flare = this.scene.add.circle(
      x + offX,
      y + offY,
      18,
      NEON.magenta,
      0.45,
    )
    flare.setBlendMode(Phaser.BlendModes.ADD)
    flare.setDepth(111)
    this.scene.tweens.add({
      targets: flare,
      scale: 2.5,
      alpha: 0,
      duration: 180,
      ease: "Quad.easeOut",
      onComplete: () => flare.destroy(),
    })

    // 4. Central white-hot "ignition" point
    const core = this.scene.add.circle(x, y, 10, COLOR.white, 0.9)
    core.setBlendMode(Phaser.BlendModes.ADD)
    core.setDepth(112)
    this.scene.tweens.add({
      targets: core,
      scale: 0.2,
      alpha: 0,
      duration: 80,
      ease: "Expo.easeOut",
      onComplete: () => core.destroy(),
    })

    // 5. Environmental impact
    this.shakeCamera(80, 0.008)
    this.flashScreen(NEON.cyan, 0.15, 100)
  }

  public emitProjectileTrail(
    x: number,
    y: number,
    color: number,
    intensity = 1,
  ): void {
    this.trailEmitter.explode(Math.max(1, Math.round(intensity)), x, y)

    const glow = this.scene.add.circle(x, y, 3.5 * intensity, color, 0.35)
    glow.setBlendMode(Phaser.BlendModes.ADD)
    glow.setDepth(88)
    this.scene.tweens.add({
      targets: glow,
      scaleX: 0.3,
      scaleY: 0.3,
      alpha: 0,
      duration: 110,
      ease: "Quad.easeOut",
      onComplete: () => glow.destroy(),
    })
  }

  public emitImpactBurst(x: number, y: number, color: number, power = 1): void {
    this.sparkEmitter.explode(Math.max(5, Math.round(10 * power)), x, y)

    // Expanding ring
    const ring = this.scene.add.circle(x, y, 6 * power)
    ring.setStrokeStyle(Math.max(1, Math.round(2 * power)), color, 0.9)
    ring.setBlendMode(Phaser.BlendModes.ADD)
    ring.setDepth(95)
    this.scene.tweens.add({
      targets: ring,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      duration: 140,
      ease: "Quad.easeOut",
      onComplete: () => ring.destroy(),
    })

    // Bright impact flash at center
    const impactFlash = this.scene.add.circle(
      x,
      y,
      3 * power,
      COLOR.white,
      0.65,
    )
    impactFlash.setBlendMode(Phaser.BlendModes.ADD)
    impactFlash.setDepth(96)
    this.scene.tweens.add({
      targets: impactFlash,
      scaleX: 1.8,
      scaleY: 1.8,
      alpha: 0,
      duration: 80,
      ease: "Quad.easeOut",
      onComplete: () => impactFlash.destroy(),
    })

    // Small smoke puff on bigger impacts
    if (power >= 0.8) {
      this.smokeEmitter.explode(Math.max(2, Math.round(4 * power)), x, y)
    }
  }

  public emitExplosionBurst(
    x: number,
    y: number,
    color: number = COLOR.orange,
    power = 1,
  ): void {
    this.explosionEmitter.explode(Math.max(10, Math.round(18 * power)), x, y)
    this.smokeEmitter.explode(Math.max(6, Math.round(10 * power)), x, y)

    // Play sprite-based explosion animation if available
    this.playExplosionAnim(x, y, power, color)

    const flash = this.scene.add.circle(x, y, 12 * power, color, 0.28)
    flash.setBlendMode(Phaser.BlendModes.ADD)
    flash.setDepth(100)
    this.scene.tweens.add({
      targets: flash,
      scaleX: 2.4,
      scaleY: 2.4,
      alpha: 0,
      duration: 180,
      ease: "Cubic.easeOut",
      onComplete: () => flash.destroy(),
    })
  }

  /** Play a sprite-based explosion animation at the given position.
   *  Picks the size variant based on power level. */
  public playExplosionAnim(
    x: number,
    y: number,
    power = 1,
    tint?: number,
  ): void {
    // Pick animation based on power
    let animKey: string
    let scale: number
    if (power >= 2) {
      animKey = "anim_explosion_neon"
      scale = 0.8 + power * 0.1
    } else if (power >= 1.2) {
      animKey = EXPLOSION_ANIMS.medium
      scale = 0.8 + power * 0.2
    } else if (power >= 0.6) {
      animKey = EXPLOSION_ANIMS.small
      scale = 0.7 + power * 0.3
    } else {
      animKey = EXPLOSION_ANIMS.micro
      scale = 0.6 + power * 0.4
    }

    // Only play if animation exists
    if (!this.scene.anims.exists(animKey)) return

    const sprite = this.scene.add.sprite(x, y, "")
    sprite.setDepth(98)
    sprite.setScale(scale)
    sprite.setBlendMode(Phaser.BlendModes.ADD)
    sprite.setAlpha(0.85)
    if (tint) sprite.setTint(tint)

    sprite.play(animKey)
    sprite.once("animationcomplete", () => sprite.destroy())
  }

  /** Play a debris/ground explosion (wider, used for big enemies or boss) */
  public playDebrisExplosion(x: number, y: number, power = 1): void {
    if (!this.scene.anims.exists(EXPLOSION_ANIMS.debris)) return

    this.explosionEmitter.explode(Math.max(12, Math.round(24 * power)), x, y)
    this.smokeEmitter.explode(Math.max(8, Math.round(14 * power)), x, y)

    const sprite = this.scene.add.sprite(x, y, "")
    sprite.setDepth(98)
    sprite.setScale(0.6 + power * 0.2)
    sprite.setBlendMode(Phaser.BlendModes.ADD)
    sprite.setAlpha(0.9)
    sprite.play(EXPLOSION_ANIMS.debris)
    sprite.once("animationcomplete", () => sprite.destroy())

    // Secondary smaller explosion offset
    this.scene.time.delayedCall(80, () => {
      this.playExplosionAnim(
        x + Phaser.Math.Between(-20, 20),
        y + Phaser.Math.Between(-15, 15),
        power * 0.6,
      )
    })

    this.shakeCamera(200, 0.02 * power)
    this.flashScreen(COLOR.orange, 0.25, 120)
  }

  /**
   * Briefly pause the game world for impact feel.
   */
  public hitStop(durationMs = 40): void {
    this.scene.physics.pause()
    this.scene.tweens.pauseAll()

    this.scene.time.delayedCall(durationMs, () => {
      this.scene.physics.resume()
      this.scene.tweens.resumeAll()
    })
  }

  /**
   * Pop up some animated floating text.
   */
  /** Play a sprite-based hit effect animation at an impact point. */
  public playHitEffect(x: number, y: number, hitAnimKey?: string): void {
    const key = hitAnimKey ?? "anim_fx_hit_1"
    if (!this.scene.anims.exists(key)) return

    const sprite = this.scene.add.sprite(x, y, "")
    sprite.setDepth(97)
    sprite.setScale(1.5)
    sprite.setBlendMode(Phaser.BlendModes.ADD)
    sprite.setAlpha(0.9)
    sprite.play(key)
    sprite.once("animationcomplete", () => sprite.destroy())
  }

  public showFloatingText(
    x: number,
    y: number,
    text: string,
    color: string = THEME.colors.whiteText,
    fontSize = "16px",
  ): void {
    const txt = this.scene.add.text(x, y, text, {
      fontSize,
      fontFamily: FONT.sans,
      fontStyle: "900",
      color,
      stroke: THEME.colors.blackText,
      strokeThickness: 3,
    })
    txt.setOrigin(0.5).setDepth(500)

    this.scene.tweens.add({
      targets: txt,
      y: y - 40,
      x: x + (Math.random() - 0.5) * 20,
      alpha: 0,
      scale: 1.5,
      duration: 600,
      ease: "Cubic.easeOut",
      onComplete: () => txt.destroy(),
    })
  }

  public playChainLightning(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ): void {
    const dist = Phaser.Math.Distance.Between(x1, y1, x2, y2)
    if (dist < 1) return

    const g = this.scene.add.graphics()
    g.setDepth(95)

    // Draw 2 jagged bolt layers (white core + colored fringe)
    const drawBolt = (lineWidth: number, color: number, alpha: number) => {
      g.lineStyle(lineWidth, color, alpha)
      g.beginPath()
      g.moveTo(x1, y1)
      const segments = 7
      const perpX = -(y2 - y1) / dist
      const perpY = (x2 - x1) / dist
      for (let i = 1; i < segments; i++) {
        const t = i / segments
        const mx = Phaser.Math.Linear(x1, x2, t)
        const my = Phaser.Math.Linear(y1, y2, t)
        const offset = (Math.random() - 0.5) * 22
        g.lineTo(mx + perpX * offset, my + perpY * offset)
      }
      g.lineTo(x2, y2)
      g.strokePath()
    }

    drawBolt(3, COLOR.info, 0.5)
    drawBolt(1, COLOR.white, 0.95)

    // Flash rings at each endpoint
    const ring1 = this.scene.add
      .circle(x1, y1, 10, COLOR.white, 0.6)
      .setDepth(96)
    const ring2 = this.scene.add
      .circle(x2, y2, 10, COLOR.info, 0.7)
      .setDepth(96)
    ring1.setBlendMode(Phaser.BlendModes.ADD)
    ring2.setBlendMode(Phaser.BlendModes.ADD)

    this.scene.tweens.add({
      targets: [g, ring1, ring2],
      alpha: 0,
      duration: 300,
      ease: "Quad.easeOut",
      onComplete: () => {
        g.destroy()
        ring1.destroy()
        ring2.destroy()
      },
    })
  }

  // ── Combat ball VFX ────────────────────────────────────────────

  /** Expanding ring flash when the ball changes state (e.g. base → charged). */
  public emitBallStateChange(x: number, y: number, toState: string): void {
    const stateColors: Record<string, number> = {
      charged: COLOR.warning,
      explosive: COLOR.danger,
      piercing: COLOR.cyanBright,
      overdrive: COLOR.purple,
      base: COLOR.info,
    }
    const color = stateColors[toState] ?? COLOR.white

    this.sparkEmitter.explode(8, x, y)

    const ring = this.scene.add.circle(x, y, 8)
    ring.setStrokeStyle(2, color, 1)
    ring.setBlendMode(Phaser.BlendModes.ADD)
    ring.setDepth(95)

    this.scene.tweens.add({
      targets: ring,
      scaleX: 3.5,
      scaleY: 3.5,
      alpha: 0,
      duration: 220,
      ease: "Quad.easeOut",
      onComplete: () => ring.destroy(),
    })

    this.hitStop(60)
  }

  /** Brief implosion burst at the left-edge drain point. */
  public emitBallDrain(x: number, y: number): void {
    this.sparkEmitter.explode(6, x, y)

    const flash = this.scene.add.circle(x, y, 10, COLOR.danger, 0.5)
    flash.setBlendMode(Phaser.BlendModes.ADD)
    flash.setDepth(95)

    this.scene.tweens.add({
      targets: flash,
      scaleX: 0.1,
      scaleY: 0.1,
      alpha: 0,
      duration: 180,
      ease: "Quad.easeIn",
      onComplete: () => flash.destroy(),
    })
  }

  // ── Floating damage numbers ──────────────────────────────────

  /** Show a floating damage number that drifts up and fades out (Brotato-style) */
  public showDamageNumber(
    x: number,
    y: number,
    damage: number,
    color: number = COLOR.white,
    isCrit = false,
  ): void {
    const offsetX = (Math.random() - 0.5) * 24
    const offsetY = (Math.random() - 0.5) * 12 - 12

    const finalColor = isCrit ? COLOR.warning : color
    const baseFontSize = isCrit ? 18 : damage >= 30 ? 14 : damage >= 10 ? 11 : 9
    const fontSize = `${baseFontSize}px`

    const text = this.scene.add
      .text(x + offsetX, y + offsetY, Math.floor(damage).toString(), {
        fontSize,
        fontFamily: FONT.sans,
        fontStyle: "900",
        color: Phaser.Display.Color.IntegerToColor(finalColor).rgba,
        stroke: THEME.colors.blackText,
        strokeThickness: isCrit ? 3 : 2,
      })
      .setOrigin(0.5)
      .setDepth(200)

    if (isCrit) {
      // Gold glow for crits
      text.setShadow(0, 0, "#fbbf24aa", 8, true, true)

      // Dramatic initial scale pop
      text.setScale(0.5)
      this.scene.tweens.add({
        targets: text,
        scale: 1.25,
        duration: 120,
        ease: "Back.easeOut",
      })
    }

    this.scene.tweens.add({
      targets: text,
      y: text.y - (isCrit ? 60 : 40),
      alpha: 0,
      duration: isCrit ? 1000 : 800,
      ease: isCrit ? "Cubic.easeOut" : "Quad.easeOut",
      onComplete: () => text.destroy(),
    })

    if (isCrit) {
      // Subtle shake for crits
      this.scene.tweens.add({
        targets: text,
        x: text.x + (Math.random() - 0.5) * 4,
        duration: 50,
        yoyo: true,
        repeat: 4,
      })
    }
  }

  /** Flash an enemy sprite with a scale punch and tint to show it took damage */
  public flashEnemyHit(
    sprite: Phaser.GameObjects.Sprite,
    color: number = COLOR.white,
    hpPercent = 1,
  ): void {
    // Compute red tint intensity based on HP (more red as HP gets lower)
    const redIntensity = hpPercent < 0.5 ? 0.8 : hpPercent < 0.75 ? 0.5 : 0.25

    // Tint flash with impact color, then transition to red damage tint
    sprite.setTint(color)
    this.scene.time.delayedCall(50, () => {
      if (!sprite.active) return
      // Apply damage-dependent red tint based on HP
      const redTint = Phaser.Display.Color.GetColor(
        Math.floor(255 * redIntensity),
        Math.floor(80 * (1 - redIntensity)),
        Math.floor(80 * (1 - redIntensity)),
      )
      sprite.setTint(redTint)
    })
    this.scene.time.delayedCall(180, () => {
      if (sprite.active) sprite.clearTint()
    })

    // Scale punch — bigger punch for lower HP
    const punchAmount = hpPercent < 0.25 ? 1.4 : hpPercent < 0.5 ? 1.3 : 1.2
    const baseScaleX = sprite.scaleX
    const baseScaleY = sprite.scaleY
    this.scene.tweens.add({
      targets: sprite,
      scaleX: baseScaleX * punchAmount,
      scaleY: baseScaleY * punchAmount,
      duration: 45,
      ease: "Quad.easeOut",
      yoyo: true,
      yoyoDelay: 30,
      onComplete: () => {
        if (sprite.active) {
          sprite.setScale(baseScaleX, baseScaleY)
        }
      },
    })

    // Hit sparks
    this.sparkEmitter.explode(
      Math.round(6 * (1.5 - hpPercent)),
      sprite.x,
      sprite.y,
    )

    // Hit ring centered on the enemy
    const ring = this.scene.add.circle(sprite.x, sprite.y, 10, color, 0)
    ring.setStrokeStyle(2, color, 1.0)
    ring.setBlendMode(Phaser.BlendModes.ADD)
    ring.setDepth(92)
    this.scene.tweens.add({
      targets: ring,
      scaleX: 3.0,
      scaleY: 3.0,
      alpha: 0,
      duration: 200,
      ease: "Quad.easeOut",
      onComplete: () => ring.destroy(),
    })
  }

  /**
   * Draw a persistent trail stamp onto the dynamic texture.
   */
  public drawPersistentTrail(
    x: number,
    y: number,
    color: number,
    scale = 1,
  ): void {
    if (!this.trailTexture) return

    // Draw a small glowing circle to the trail texture
    this.trailTexture.stamp(TEX.PARTICLE, undefined, x, y, {
      alpha: 0.4,
      tint: color,
      scale: scale,
      blendMode: Phaser.BlendModes.ADD,
    })
  }

  /**
   * Fade out the persistent trail texture over time.
   * Should be called every frame.
   */
  public updateTrails(delta: number): void {
    if (!this.trailTexture) return

    // Slowly fade the entire texture
    const fadeAmount = 0.05 * (delta / 16)
    this.trailTexture.fill(0x000000, fadeAmount)
  }

  /**
   * Create a screen-space shockwave effect at a world position.
   * Uses CaptureFrame to grab the current scene and a Barrel filter for distortion.
   */
  public emitShockwave(x: number, y: number, power = 1): void {
    const captureKey = this.nextCaptureKey(this.captureKey)
    const capture = this.captureFrame(captureKey)
    capture.setDepth(150)

    // We need a display object to show the captured texture with a filter
    const overlay = this.scene.add.image(x, y, captureKey)
    overlay.setDepth(151)
    overlay.setDisplaySize(200 * power, 200 * power)

    overlay.enableFilters()
    const barrel = overlay.filters?.internal.addBarrel(1.0)
    if (!barrel) {
      overlay.destroy()
      capture.destroy()
      this.removeTexture(captureKey)
      return
    }

    this.scene.tweens.add({
      targets: barrel,
      amount: { from: 0.85, to: 1.0 },
      duration: 300,
      ease: "Quad.easeOut",
    })

    this.scene.tweens.add({
      targets: overlay,
      scale: 2.5,
      alpha: 0,
      duration: 350,
      ease: "Quad.easeOut",
      onComplete: () => {
        overlay.destroy()
        capture.destroy()
        this.removeTexture(captureKey)
      },
    })
  }

  /**
   * Create a large-scale warp ripple effect (e.g. for sector transitions).
   */
  public emitWarpRipple(): void {
    const cam = this.mainCamera
    if (!cam) return
    const captureKey = this.nextCaptureKey("warp_capture")
    const capture = this.captureFrame(captureKey)
    capture.setDepth(1000)

    const overlay = this.scene.add.image(cam.centerX, cam.centerY, captureKey)
    overlay.setDepth(1001)
    overlay.setScrollFactor(0)

    overlay.enableFilters()
    const barrel = overlay.filters?.internal.addBarrel(1.0)
    if (!barrel) {
      overlay.destroy()
      capture.destroy()
      this.removeTexture(captureKey)
      return
    }

    this.scene.tweens.add({
      targets: barrel,
      amount: { from: 0.6, to: 1.0 },
      duration: 800,
      ease: "Cubic.easeOut",
    })

    this.scene.tweens.add({
      targets: overlay,
      scale: 1.2,
      alpha: 0,
      duration: 900,
      ease: "Cubic.easeOut",
      onComplete: () => {
        overlay.destroy()
        capture.destroy()
        this.removeTexture(captureKey)
      },
    })
  }

  private nextCaptureKey(prefix: string): string {
    this.captureIndex += 1
    return `${prefix}_${this.captureIndex}`
  }

  private captureFrame(key: string): CaptureFrameObject {
    return (this.scene.add as CaptureFrameFactory).captureFrame(key)
  }

  private removeTexture(key: string): void {
    if (this.scene.textures.exists(key)) {
      this.scene.textures.remove(key)
    }
  }

  /**
   * Apply a subtle persistent vignette and cyberpunk color grade to the gameplay
   * camera. Idempotent — safe to call multiple times; skips already-active layers.
   * Call once after createSystems() and after every filter list clear.
   */
  public applyBaseCameraFx(): void {
    const cam = this.mainCamera
    if (!cam) return
    if (!this.baseCameraVignette) {
      this.baseCameraVignette = cam.filters.external.addVignette(
        0.5,
        0.5,
        0.72,
        0.28,
      )
    }
    if (
      !this.baseCameraColorMatrix ||
      !this.baseCameraColorMatrix.colorMatrix
    ) {
      this.baseCameraColorMatrix = cam.filters.internal.addColorMatrix()
      const cm = this.baseCameraColorMatrix?.colorMatrix
      if (cm) {
        cm.saturate(0.2)
        cm.contrast(0.08)
      }
    }
  }

  /**
   * Clear only external camera filters (e.g. to remove a status vignette) then
   * restore the base vignette so the gameplay atmosphere is preserved.
   */
  public clearExternalAndRestoreBase(): void {
    this.mainCamera?.filters.external.clear()
    this.baseCameraVignette = null
    this.applyBaseCameraFx()
  }

  /**
   * Apply overdrive mode screen treatment — a restrained matrix-like slow motion tint.
   * Call when entering overdrive mode.
   * @param activationColor - The color to theme the overdrive visuals (most frequent bumper color)
   */
  public emitOverdriveScreen(activationColor = 0x4c1d95): void {
    const cam = this.mainCamera
    if (!cam) return

    const cm = cam.filters.internal.addColorMatrix()
    if (cm.colorMatrix) {
      cm.colorMatrix.saturate(0.3)
      cm.colorMatrix.contrast(0.12)
      cm.colorMatrix.brightness(0.92)
    }

    const pf = cam.filters.internal.addParallelFilters()
    pf.top.addThreshold(0.42, 1.0)
    pf.top.addBlur(0, 3, 3, 1.4)
    pf.blend.blendMode = Phaser.BlendModes.ADD
    pf.blend.amount = 0.34

    cam.filters.external.addVignette(
      0.5,
      0.5,
      0.76,
      0.36,
      0x05070d,
      Phaser.BlendModes.MULTIPLY,
    )
    cam.filters.external.addVignette(
      0.5,
      0.5,
      0.92,
      0.12,
      activationColor,
      Phaser.BlendModes.ADD,
    )
  }

  /** Remove overdrive screen treatment and restore base camera FX. */
  public clearOverdriveScreen(): void {
    const cam = this.mainCamera
    if (!cam) return
    cam.filters.external.clear()
    cam.filters.internal.clear()
    this.baseCameraVignette = null
    this.baseCameraColorMatrix = null
    this.comboEscalationMatrix = null
    this.applyBaseCameraFx()
  }

  /**
   * Apply combo tier visual escalation — brightness increases with combo tier.
   * Call on combo tier changes.
   */
  public applyComboEscalation(multiplier: number): void {
    const cam = this.mainCamera
    if (!cam) return
    const intensity = Math.min(1, (multiplier - 1) / 4)
    if (
      !this.comboEscalationMatrix ||
      !this.comboEscalationMatrix.colorMatrix
    ) {
      this.comboEscalationMatrix = cam.filters.internal.addColorMatrix()
    }

    const cm = this.comboEscalationMatrix?.colorMatrix
    if (cm) {
      cm.reset()
      cm.brightness(1.0 + intensity * 0.35)
    }
  }

  /** Clear combo escalation and restore base internal color grade. */
  public clearComboEscalation(): void {
    this.mainCamera?.filters.internal.clear()
    this.baseCameraColorMatrix = null
    this.comboEscalationMatrix = null
    this.applyBaseCameraFx()
  }

  /** Apply boss intro threshold effect — bright warning glow. */
  public applyBossIntro(): void {
    const cam = this.mainCamera
    if (!cam) return
    const cm = cam.filters.internal.addColorMatrix()
    if (cm.colorMatrix) {
      cm.colorMatrix.brightness(0.3)
    }
    cam.filters.external.addGlow(0xff0000, 4, 0, 1)
  }

  /** Clear boss intro effect and restore base camera FX. */
  public clearBossIntro(): void {
    const cam = this.mainCamera
    if (!cam) return
    cam.filters.external.clear()
    cam.filters.internal.clear()
    this.baseCameraVignette = null
    this.baseCameraColorMatrix = null
    this.comboEscalationMatrix = null
    this.applyBaseCameraFx()
  }

  /**
   * Draw a large glowing "Wave Cannon" laser beam.
   * @param x Origin X
   * @param y Origin Y
   * @param angle Direction in radians
   * @param progress 0-1 (warning phase to full fire)
   * @param color Beam color
   */
  public drawWaveCannon(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    angle: number,
    progress: number,
    color: number = NEON.red,
  ): void {
    const length = GAME_WIDTH * 1.5
    const cosA = Math.cos(angle)
    const sinA = Math.sin(angle)
    const endX = x + cosA * length
    const endY = y + sinA * length

    if (progress < 0.4) {
      // Warning line: thin dashed or flickering
      const alpha = 0.2 + Math.sin(this.scene.time.now / 30) * 0.1
      gfx.lineStyle(2, color, alpha)
      gfx.lineBetween(x, y, endX, endY)
    } else {
      // Full fire: thick layered beam
      const fireProgress = (progress - 0.4) / 0.6
      const width = 40 * fireProgress
      const coreWidth = 12 * fireProgress

      // Outer glow
      gfx.lineStyle(width * 2, color, 0.15)
      gfx.lineBetween(x, y, endX, endY)

      // Mid glow
      gfx.lineStyle(width, color, 0.3)
      gfx.lineBetween(x, y, endX, endY)

      // Core
      gfx.lineStyle(coreWidth, COLOR.white, 0.9)
      gfx.lineBetween(x, y, endX, endY)

      // Particles at origin
      if (Math.random() > 0.5) {
        this.muzzleEmitter.explode(2, x, y)
      }
    }
  }
}
